// libs
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConnectedRouter as Router } from 'connected-react-router';
import { loadableReady } from '@loadable/component';
import { HelmetProvider } from 'react-helmet-async';

// store
import { configureStore } from 'shared/store';
import createHistory from 'shared/store/history';

// components
import App from 'shared/App';
import { browserResponsiveMode } from 'shared/components/ResponsiveLoader';

// helpers
import polyfillPromise from 'shared/helpers/client-polyfill';
import { swAllowed, isDevelopment } from 'shared/helpers/utils';
import { setGlobals } from 'shared/helpers/globals';
import { addPageAction, log } from 'shared/helpers/newRelic';
import { DataFetchingProvider } from 'shared/helpers/dataFetching';
import { buildStoreRedirectId, DEFAULT_STORE } from 'shared/helpers/storeUrl';
import { sendDataToGTM } from 'shared/helpers/analytics';

// constants
import { TAG_IDENTIFIER, ERROR_TAGS } from 'shared/helpers/newRelic/constants';

// Create/use the store
// history MUST be passed here if you want syncing between server on initial route

const preloadStateEl = document.getElementById('__PRELOADED_STATE__');
const appDataEl = document.getElementById('__APP_DATA__');

const initialState = preloadStateEl ? JSON.parse(preloadStateEl.textContent || '{}') : {};
const appData = appDataEl ? JSON.parse(appDataEl.textContent || '{}') : {};

// Determine basename for history based on storeId
const storeId = appData.storeId || DEFAULT_STORE;

const STORE_TRACKING_KEY = 'nf_previous_store';
let previousStore = DEFAULT_STORE;
try {
  previousStore = localStorage.getItem(STORE_TRACKING_KEY) || DEFAULT_STORE;
  localStorage.setItem(STORE_TRACKING_KEY, storeId);
} catch {
  // Safari Private Browsing — non-fatal
}

// @ts-ignore
window.addEventListener('load', () => {
  try {
    const nr = (window as any).newrelic;
    nr?.setCustomAttribute('resolvedStoreId', storeId);
    nr?.setCustomAttribute('previousStore', previousStore);
    nr?.setCustomAttribute('storeChanged', String(previousStore !== storeId));

    // Fallback: if banner redirect params exist on landing URL, emit page action
    // from destination page to avoid source-page unload drops.
    const params = new URLSearchParams(window.location.search);
    const triggerType = params.get('transition_trigger_type');
    const triggerLocation = params.get('transition_location');
    if (triggerType && triggerLocation) {
      addPageAction('storeBannerSwitch', {
        fromStore: previousStore,
        toStore: storeId,
        triggerType,
        triggerLocation,
        searchTerm: params.get('q') || undefined,
      });
    }
  } catch {
    // non-fatal
  }
});

sendDataToGTM({
  storeId: storeId === DEFAULT_STORE ? 'fashion' : storeId,
  currentStore: buildStoreRedirectId(storeId),
  previousStore: buildStoreRedirectId(previousStore),
  storeChanged: previousStore !== storeId,
});

const basename = storeId !== DEFAULT_STORE ? `/${storeId}` : undefined;

const history = createHistory({ basename });

const existingStore = window.store;
const store =
  existingStore &&
  typeof existingStore.getState === 'function' &&
  typeof existingStore.dispatch === 'function'
    ? existingStore
    : configureStore({
        initialState,
        history,
        storeId,
      });

// set all the values that should be present on client before render
setGlobals(initialState);

polyfillPromise.then(() => {
  loadableReady(() => {
    const { deviceWidth: browserWidth } = browserResponsiveMode();
    const deviceWidth = appData.ssrDeviceWidth || browserWidth;

    hydrateRoot(
      document.getElementById('app'),
      <HelmetProvider>
        <Provider store={store}>
          <Router history={history}>
            <DataFetchingProvider skipInitialClientFetch>
              <App deviceWidth={deviceWidth} />
            </DataFetchingProvider>
          </Router>
        </Provider>
      </HelmetProvider>
    );
  });
});

if (isDevelopment) {
  if (module.hot) {
    module.hot.accept();
  }

  window.store = store;
}

// Check that service workers are supported
if ('serviceWorker' in navigator && swAllowed) {
  // Use the window load event to keep the page load performant
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err: any) => {
      log({
        errTitle: 'Error in service worker',
        err: err,
        tags: { [TAG_IDENTIFIER.MODULE]: ERROR_TAGS.SERVICE_WORKER },
      });
    });
  });
}
